import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CommonTable from "../CommonTable";
import { BASE_URL } from "../../api";
import { getApiHeaders, formatDateDDMMYYYY } from "../../utils/helpers";

export default function PointHistory({ history = [], onViewDetails, onDataLoaded }) {
  const navigate = useNavigate();
  const [data, setData] = useState(Array.isArray(history) ? history : []);
  const [loading, setLoading] = useState(false);

  const fetchPointHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/account/learn_earn_point_history`, {
        headers: getApiHeaders(),
      });

      if (
        (res.data?.code === 200 || res.data?.status === "success") &&
        res.data?.data
      ) {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : [raw];
        setData(list);
        if (onDataLoaded) onDataLoaded(list);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching learn & earn point history:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPointHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "var(--bs-card-bg, #ffffff)",
    zIndex: 2,
    boxShadow: "0 1px 0 var(--bs-border-color, #e9ecef)",
  };

  const columns = [
    {
      header: "Level No",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="fw-semibold">
          {row.level_no ?? row.levelNo ?? "-"}
        </span>
      ),
    },
    {
      header: "Level Name",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="fw-medium">
          {row.level_name ?? row.levelName ?? "-"}
        </span>
      ),
    },
    // {
    //   header: "Day",
    //   headerStyle: stickyHeaderStyle,
    //   render: (row) => (
    //     <span className="badge bg-light text-dark border px-2 py-1">
    //       {row.day_no ?? row.day ?? "-"}
    //     </span>
    //   ),
    // },
    {
      header: "Point",
      headerStyle: stickyHeaderStyle,
      render: (row) => {
        const pts = row.points ?? row.point ?? 0;
        const isDebit = String(row.type || "").toLowerCase() === "debit";
        return (
          <span className={`fw-bold font-size-13 ${isDebit ? "text-danger" : "text-success"}`}>
            {isDebit ? `-${Math.abs(Number(pts))}` : `+${pts}`}
          </span>
        );
      },
    },
    {
      header: "Type",
      headerClassName: "text-center",
      headerStyle: {
        ...stickyHeaderStyle,
        textAlign: "center",
      },
      className: "text-center",
      render: (row) => {
        const isCredit = String(row.type || "").toLowerCase() === "credit";
        const rawStatus = row.status ?? row.claim_status ?? row.claimStatus ?? row.istatus;

        let statusBadge = null;
        if (
          rawStatus !== undefined &&
          rawStatus !== null &&
          String(rawStatus).trim() !== "" &&
          String(rawStatus).trim() !== "-" &&
          String(rawStatus).toLowerCase() !== "null" &&
          String(rawStatus).toLowerCase() !== "undefined"
        ) {
          const s = String(rawStatus).toLowerCase().trim();
          let badgeClass = "bg-secondary bg-opacity-10 text-secondary";
          let label = String(rawStatus).trim();

          if (s.includes("pend") || s.includes("pending") || s === "processing") {
            badgeClass = "bg-warning bg-opacity-10 text-warning";
            label = "Pending";
          } else if (
            s.includes("confirm") ||
            s.includes("approve") ||
            s.includes("success") ||
            s.includes("complete")
          ) {
            badgeClass = "bg-success bg-opacity-10 text-success";
            label = "Approve";
          } else if (
            s.includes("reject") ||
            s.includes("fail")
          ) {
            badgeClass = "bg-danger bg-opacity-10 text-danger";
            label = "Reject";
          }

          statusBadge = (
            <span className={`badge fw-bold px-3 py-2 rounded-pill ms-2 ${badgeClass}`}>
              {label}
            </span>
          );
        }

        return (
          <div className="d-inline-flex align-items-center justify-content-center">
            <span
              className={`badge fw-bold px-3 py-2 rounded-pill ${isCredit
                ? "bg-success bg-opacity-10 text-success"
                : "bg-danger bg-opacity-10 text-danger"
                }`}
            >
              {row.type || "-"}
            </span>
            {statusBadge}
          </div>
        );
      },
    },
    {
      header: "Date",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="font-size-12 text-muted">
          {formatDateDDMMYYYY(row.ondate || row.insertedOn || row.created_at) || "-"}
        </span>
      ),
    },
  ];

  const handleViewResult = () => {
    const item = data && data.length > 0 ? data[0] : null;
    const pts = item?.points ?? item?.point ?? 0;
    const day = item?.day_no ?? item?.day ?? 1;
    const formattedItem = {
      ...(item || {}),
      id: item?.id || `point-${day}`,
      levelNo: item?.level_no ?? item?.levelNo ?? 1,
      levelName: item?.level_name ?? item?.levelName ?? "Basic",
      day: day,
      point: `${pts} (${((Number(pts) || 0) / 500).toFixed(2)} - USD)`,
      earnedPts: `${pts} pts earned`,
      usdValue: `${((Number(pts) || 0) / 500).toFixed(2)} USD`,
      type: item?.type || "Credit",
      insertedOn: formatDateDDMMYYYY(item?.ondate || item?.insertedOn || item?.created_at || item?.datetime),
      taskName: item?.task_name || item?.taskName || `Level ${item?.level_no ?? 1} - Day ${day} Task`,
      score: item?.score || `${Number(pts) > 0 ? "5 / 5 Correct" : "0 / 5 Correct"}`,
      potential: `${pts > 0 ? pts : 10} pts`,
    };

    if (onViewDetails) {
      onViewDetails(formattedItem);
    } else {
      navigate("/point-details", { state: { pointData: formattedItem } });
    }
  };

  return (
    <div className="card h-100 shadow-sm border-0 mb-0">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="card-title mb-0">Point History</h4>
          <button
            type="button"
            className="btn btn-primary btn-sm waves-effect waves-light px-3"
            onClick={handleViewResult}
          >
            View Result
          </button>
        </div>

        <CommonTable
          columns={columns}
          data={data}
          loading={loading}
          disablePagination={true}
          containerStyle={{
            height: "560px",
            maxHeight: "560px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
          loadingMessage={
            <div>
              <div
                className="spinner-border text-primary spinner-border-sm me-2"
                role="status"
              ></div>
              <span>Loading point history...</span>
            </div>
          }
          noDataMessage="No point history records found"
          tableClassName="table table-hover align-middle table-nowrap mb-0"
        />
      </div>
    </div>
  );
}
